/**
 * Rich content AST for chat bubbles (Fase 31).
 *
 * Tres canales WS distintos comparten un mismo formato (`RichMessage`) y un
 * mismo renderer (`<RichMessageBody>`):
 *
 *  - Opener del módulo (lifecycle, una vez por sesión) — viaja en
 *    `serverSessionStartedSchema.openerAssistantContent`.
 *  - Tool results mid-sesión — evento WS `tool.result`.
 *  - Distill / summary al cierre — reusa `tool.result` con un `name`
 *    reservado (ej. `'session.distill'`). MVP solo reserva el namespace.
 *
 * Sin parser markdown runtime: el AST llega ya parseado. Sanitización =
 * validación Zod estricta + escape default de React + whitelist de URL schemes.
 */

import { z } from 'zod';

/* -------------------------------------------------------------------------- */
/* Caps                                                                       */
/* -------------------------------------------------------------------------- */

export const RICH_LIMITS = {
  maxBlocks: 50,
  maxInlines: 50,
  maxParagraphChars: 4000,
  maxBullets: 20,
  maxKvPairs: 20,
  maxHeadingChars: 200,
  maxLabelChars: 200,
  maxTimestampLabelChars: 20,
  maxKvKeyChars: 80,
  maxKvValueChars: 400,
} as const;

/* -------------------------------------------------------------------------- */
/* Inline                                                                     */
/* -------------------------------------------------------------------------- */

const inlineTextSchema = z.object({
  type: z.literal('text'),
  text: z.string().min(1),
});

const inlineStrongSchema = z.object({
  type: z.literal('strong'),
  text: z.string().min(1),
});

const inlineEmSchema = z.object({
  type: z.literal('em'),
  text: z.string().min(1),
});

export const inlineSchema = z.discriminatedUnion('type', [
  inlineTextSchema,
  inlineStrongSchema,
  inlineEmSchema,
]);

export type Inline = z.infer<typeof inlineSchema>;

const inlinesSchema = z
  .array(inlineSchema)
  .min(1)
  .max(RICH_LIMITS.maxInlines)
  .refine(
    (arr) =>
      arr.reduce((sum, i) => sum + i.text.length, 0) <= RICH_LIMITS.maxParagraphChars,
    { message: `inlines exceed ${RICH_LIMITS.maxParagraphChars} total chars` },
  );

/* -------------------------------------------------------------------------- */
/* TimestampRef                                                               */
/* -------------------------------------------------------------------------- */

export const timestampRefSchema = z.object({
  ms: z.number().int().nonnegative(),
  label: z.string().min(1).max(RICH_LIMITS.maxTimestampLabelChars).optional(),
});

export type TimestampRef = z.infer<typeof timestampRefSchema>;

/* -------------------------------------------------------------------------- */
/* URL whitelist                                                              */
/* -------------------------------------------------------------------------- */

const httpsHrefSchema = z
  .string()
  .min(1)
  .max(2048)
  .refine((s) => s.startsWith('https://'), {
    message: 'href must use https:// scheme',
  });

/* -------------------------------------------------------------------------- */
/* Blocks                                                                     */
/* -------------------------------------------------------------------------- */

const headingBlockSchema = z.object({
  type: z.literal('heading'),
  text: z.string().min(1).max(RICH_LIMITS.maxHeadingChars),
  level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
});

const paragraphBlockSchema = z.object({
  type: z.literal('paragraph'),
  inlines: inlinesSchema,
});

const bulletItemSchema = z.object({
  inlines: inlinesSchema,
  ref: timestampRefSchema.optional(),
});

const bulletsBlockSchema = z.object({
  type: z.literal('bullets'),
  items: z.array(bulletItemSchema).min(1).max(RICH_LIMITS.maxBullets),
});

const linkBlockSchema = z.object({
  type: z.literal('link'),
  href: httpsHrefSchema,
  label: z.string().min(1).max(RICH_LIMITS.maxLabelChars),
});

const dividerBlockSchema = z.object({
  type: z.literal('divider'),
});

const kvPairSchema = z.object({
  key: z.string().min(1).max(RICH_LIMITS.maxKvKeyChars),
  value: z.string().min(1).max(RICH_LIMITS.maxKvValueChars),
});

const kvListBlockSchema = z.object({
  type: z.literal('kvList'),
  pairs: z.array(kvPairSchema).min(1).max(RICH_LIMITS.maxKvPairs),
});

const promptBlockSchema = z.object({
  type: z.literal('prompt'),
  inlines: inlinesSchema,
});

/**
 * Bloque `timestamp` standalone — útil cuando un timestamp aparece fuera de
 * un bullet (ej. resumen estructurado donde cada item tiene su propio ms).
 * Los bullets siguen siendo el formato preferido cuando el timestamp es un
 * accesorio del texto.
 */
const timestampBlockSchema = z.object({
  type: z.literal('timestamp'),
  ms: z.number().int().nonnegative(),
  label: z.string().min(1).max(RICH_LIMITS.maxTimestampLabelChars).optional(),
});

export const richBlockSchema = z.discriminatedUnion('type', [
  headingBlockSchema,
  paragraphBlockSchema,
  bulletsBlockSchema,
  linkBlockSchema,
  dividerBlockSchema,
  kvListBlockSchema,
  promptBlockSchema,
  timestampBlockSchema,
]);

export type RichBlock = z.infer<typeof richBlockSchema>;

/* -------------------------------------------------------------------------- */
/* RichMessage                                                                */
/* -------------------------------------------------------------------------- */

export const richMessageSchema = z
  .array(richBlockSchema)
  .min(1)
  .max(RICH_LIMITS.maxBlocks);

export type RichMessage = z.infer<typeof richMessageSchema>;

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function inlinesToPlain(inlines: ReadonlyArray<Inline>): string {
  return inlines.map((i) => i.text).join('');
}

function msToTimestamp(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  return `${mm.toString().padStart(2, '0')}:${ss.toString().padStart(2, '0')}`;
}

function blockToPlain(block: RichBlock): string {
  switch (block.type) {
    case 'heading':
      return block.text;
    case 'paragraph':
    case 'prompt':
      return inlinesToPlain(block.inlines);
    case 'bullets':
      return block.items
        .map((item) => {
          const text = inlinesToPlain(item.inlines);
          const ref = item.ref ? ` [${msToTimestamp(item.ref.ms)}]` : '';
          return `- ${text}${ref}`;
        })
        .join('\n');
    case 'link':
      return `${block.label} (${block.href})`;
    case 'divider':
      return '---';
    case 'kvList':
      return block.pairs.map((p) => `${p.key}: ${p.value}`).join('\n');
    case 'timestamp':
      return `[${msToTimestamp(block.ms)}]${block.label ? ` ${block.label}` : ''}`;
    default: {
      // Forward-compat: bloques desconocidos colapsan a vacío en plain.
      // El renderer cliente se encarga del fallback visual.
      const _exhaustive: never = block;
      void _exhaustive;
      return '';
    }
  }
}

/**
 * Reduce un `RichMessage` a su versión legible en texto plano. Usado para:
 *
 *  - Persistencia legacy `turns.assistant_text` (fallback derivado).
 *  - Accessibility (screen readers que no soportan el AST).
 *  - Logs y traces que no quieren JSON crudo.
 *
 * Mantiene saltos de línea entre bloques para preservar estructura.
 */
export function richToPlain(msg: RichMessage): string {
  return msg.map(blockToPlain).join('\n\n').trim();
}
